import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { DateTime } from '@/components/data/DateTime';
import { AuditChip } from '@/components/audit/AuditChip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { DesignerForm } from '@/features/commerce/DesignerForm';
import { useDesigners, useDeleteDesigner, useProducts } from '@/queries/commerce';
import { useApiError } from '@/lib/useApiError';
import { useUiStore } from '@/stores/ui';
import type { Designer } from '@/schemas/admin/commerce/designers';

export const Route = createFileRoute('/_authed/commerce/designers')({
  component: DesignersScreen,
});

function DesignersScreen() {
  const { t } = useTranslation('commerce');
  const { t: tc } = useTranslation('common');
  const locale = useUiStore((s) => s.locale);
  const list = useDesigners();
  const del = useDeleteDesigner();
  const resolveError = useApiError();
  // Pull the product list so we can show a per-designer product count even when
  // the designers endpoint omits `product_count` (F-AD-07).
  const productList = useProducts({ limit: 200 });

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<Designer | null>(null);
  const [deleting, setDeleting] = React.useState<Designer | null>(null);
  const [filter, setFilter] = React.useState('');

  const productCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of productList.data ?? []) {
      if (p.deleted_at) continue;
      counts.set(p.designer_id, (counts.get(p.designer_id) ?? 0) + 1);
    }
    return counts;
  }, [productList.data]);

  function localizedName(d: Designer): string {
    if (locale === 'eg-ar' && d.name_eg_ar) return d.name_eg_ar;
    if (locale === 'ar' && d.name_ar) return d.name_ar;
    if (d.name_en) return d.name_en;
    return d.name;
  }

  const filtered = React.useMemo(() => {
    const data = list.data ?? [];
    const needle = filter.trim().toLowerCase();
    if (!needle) return data;
    return data.filter((d) =>
      [
        d.slug,
        d.name,
        d.name_en ?? '',
        d.name_ar ?? '',
        d.name_eg_ar ?? '',
        d.country ?? '',
      ].some((s) => s.toLowerCase().includes(needle))
    );
  }, [list.data, filter]);

  async function handleDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success(t('designers.deleted'));
      setDeleting(null);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  const columns: readonly DataColumn<Designer>[] = [
    {
      id: 'name',
      header: t('designers.table.name'),
      cell: (r) => (
        <div className="flex flex-col">
          <span className="font-medium text-[color:var(--color-fg)]">{localizedName(r)}</span>
          <span dir="ltr" className="font-mono text-[11px] text-[color:var(--color-fg-muted)]">
            {r.slug}
          </span>
        </div>
      ),
      sortBy: (r) => localizedName(r),
    },
    {
      id: 'country',
      header: t('designers.table.country'),
      cell: (r) => r.country ?? <span className="text-[color:var(--color-fg-subtle)]">—</span>,
      sortBy: (r) => r.country ?? '',
    },
    {
      id: 'products',
      header: t('designers.table.products'),
      cell: (r) => {
        const count =
          typeof r.product_count === 'number' ? r.product_count : productCounts.get(r.id);
        if (typeof count === 'number') return count;
        if (productList.isLoading)
          return <span className="text-[color:var(--color-fg-subtle)]">…</span>;
        return <span className="text-[color:var(--color-fg-subtle)]">—</span>;
      },
      align: 'end',
      sortBy: (r) =>
        typeof r.product_count === 'number' ? r.product_count : (productCounts.get(r.id) ?? -1),
    },
    {
      id: 'state',
      header: t('designers.table.state'),
      cell: (r) =>
        r.deleted_at ? (
          <Badge tone="danger">{t('designers.states.deleted')}</Badge>
        ) : r.active === false ? (
          <Badge tone="warn">{t('designers.states.inactive')}</Badge>
        ) : (
          <Badge tone="success">{t('designers.states.active')}</Badge>
        ),
    },
    {
      id: 'created',
      header: t('designers.table.created'),
      cell: (r) => <DateTime value={r.created_at ?? null} />,
      sortBy: (r) => (r.created_at ? new Date(r.created_at) : null),
    },
    {
      id: 'actions',
      header: <span className="sr-only">{tc('table.actions')}</span>,
      align: 'end',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing(r)}
            aria-label={t('designers.edit', { name: localizedName(r) })}
          >
            <Pencil aria-hidden className="size-4" />
            <span className="sr-only md:not-sr-only md:text-xs">{tc('actions.save')}</span>
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setDeleting(r)}
            aria-label={t('designers.delete', { name: localizedName(r) })}
            disabled={Boolean(r.deleted_at)}
          >
            <Trash2 aria-hidden className="size-4" />
            <span className="sr-only md:not-sr-only md:text-xs">{tc('actions.cancel')}</span>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('designers.title')}</h1>
          <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('designers.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <AuditChip />
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden className="size-4" />
            {t('designers.new')}
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="space-y-3 pt-6">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('designers.filterPlaceholder')}
            aria-label={t('designers.filterLabel')}
            className="max-w-sm"
          />
          <DataTable<Designer>
            data={filtered}
            columns={columns}
            rowKey={(r) => r.id}
            loading={list.isLoading}
            error={list.error ? resolveError(list.error) : null}
            empty={{ title: t('designers.empty.title'), body: t('designers.empty.body') }}
            initialSort={{ id: 'name', direction: 'asc' }}
          />
        </CardContent>
      </Card>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('designers.new')}</DialogTitle>
            <DialogDescription>
              {t('designers.newBody', {
                defaultValue:
                  'Add a designer to the catalog. Slug is immutable once saved.',
              })}
            </DialogDescription>
          </DialogHeader>
          <DesignerForm onSaved={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('designers.editTitle')}</DialogTitle>
            <DialogDescription>
              {t('designers.editBody', {
                defaultValue:
                  'Update the designer name, country, bio, and active state. Linked products stay attached.',
              })}
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <DesignerForm
              designer={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={t('designers.deleteTitle')}
        description={t('designers.deleteBody', { name: deleting ? localizedName(deleting) : '' })}
        destructive
        requireType="DELETE"
        confirmLabel={t('designers.deleteConfirm')}
        loading={del.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}
